import { TileWMS, WMTS } from 'ol/source';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { apiBaseUrl } from 'client/utils/constants/frontEndConfig';
import { getAccessToken } from 'client/utils/auth/msalHelpers';
import { useEffect, useState } from 'react';
import { getBaseMapProperties } from 'client/utils/helpers/MapConfig/MapConfig';

/**
 * Custom tile loader function which modifies the request to include the access token as
 * an Authorization header.
 * @param {Object} tile - the tile.
 * @param {Object} source - the source.
 */
const withAuth = async (tile, source) => {
    const client = new XMLHttpRequest();
    client.responseType = 'blob';
    client.open('GET', source);
    const accessToken = await getAccessToken();
    client.setRequestHeader('Authorization', accessToken);
    client.onload = () => {
        tile.getImage().src = URL.createObjectURL(client.response);
    };
    client.send();
};

// Hardcoded 27700 for rare cases where we need to use this projection no matter what the user is set up for
// e.g. network coverage page
const _matrixIds27700 = [];
for (let z = 0; z < 14; z += 1) {
    _matrixIds27700.push(`EPSG:27700:${z}`);
}

const Light27700 = new WMTS({
    url: `${apiBaseUrl}/os-maps/maps/raster/v1/wmts`,
    projection: 'EPSG:27700',
    layer: 'Light_27700',
    format: 'image/png',
    matrixSet: 'EPSG:27700',
    tileGrid: new WMTSTileGrid({
        origin: [-238375.0, 1376256.0],
        resolutions: [896.0, 448.0, 224.0, 112.0, 56.0, 28.0, 14.0, 7.0, 3.5, 1.75, 0.875, 0.4375, 0.21875, 0.109375],
        matrixIds: _matrixIds27700,
        extent: [-225000.0, 0.0, 850000.0, 1350000.0]
    }),
    // At the moment we are not going to translate anything related to the base map (i.e. attributions)
    // Although... we totally could so idk why not.
    attributions: ['Contains OS data &copy; Crown copyright and database rights ' + new Date().getFullYear()],
    tileLoadFunction: withAuth
});

const layerSourceFromMapConfigBaseMap = (baseMap) => {
    const { sourceProperties } = baseMap;
    if (!sourceProperties) {
        // eslint-disable-next-line no-console
        console.error('Using default base map');
        return Light27700;
    }

    const sourceType = baseMap.sourceType.toUpperCase();

    if (sourceType === 'WMTS') {
        const tileGridProperties = JSON.parse(sourceProperties.tileGrid);
        return new WMTS({
            ...sourceProperties,
            url: `${apiBaseUrl}${sourceProperties.url}`,
            tileGrid: new WMTSTileGrid(tileGridProperties),
            tileLoadFunction: withAuth
        });
    } else if (sourceType === 'WMS') {
        return new TileWMS({
            ...sourceProperties,
            url: `${apiBaseUrl}${sourceProperties.url}`,
            projection: sourceProperties.crs,
            params: {
                LAYERS: [sourceProperties.layers],
                STYLES: 'default'
            },
            tileLoadFunction: withAuth
        });
    }

    throw new Error(`Unknown base map source type ${sourceType}`);
};

const useBaseMap = () => {
    const [baseMapSource, setBaseMapSource] = useState(null);
    useEffect(() => {
        // Set the base map according to the map config.
        getBaseMapProperties().then(baseMap => {
            setBaseMapSource(layerSourceFromMapConfigBaseMap(baseMap));
        });
    }, []);

    return baseMapSource;
};

export { useBaseMap, withAuth };
